/**
 * Tiny Web Audio manager.
 *
 * iPhone Safari quirks:
 *   - AudioContext is created suspended; must be resumed inside a user
 *     gesture handler. We expose unlock() and call it from the first
 *     pointerdown via Input.onFirstInteract.
 *   - Once unlocked, the context stays alive for the page's lifetime.
 *   - We synthesize SFX via oscillators so we ship with zero binary
 *     audio assets in v1.
 */
type SfxKind = "coin" | "powerup" | "crash" | "swipe" | "jump" | "slide" | "complete";

// Skip the song's intro — gameplay music starts (and re-loops) from here.
const MUSIC_START_OFFSET = 30;

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private music: HTMLAudioElement | null = null;
  private menu: HTMLAudioElement | null = null;
  // iOS Safari blocks HTMLAudioElement.play() outside a user gesture and
  // the gesture "window" expires fast. When toMenu() / startRun() fire
  // after an async hop (auth fetch, intro video) the play() often rejects.
  // Track which tracks *want* to be playing, and retry on every subsequent
  // user gesture until they do.
  private wantMenu = false;
  private wantMusic = false;

  init(): void {
    if (this.ctx) return;
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.4;
    this.master.connect(this.ctx.destination);
  }

  unlock(): void {
    this.init();
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") this.ctx.resume();
    // 1-sample silent buffer — definitively unlocks playback on iOS.
    const buf = this.ctx.createBuffer(1, 1, 22050);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    src.start(0);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.4;
    if (this.music) this.music.volume = m ? 0 : 0.6;
    if (this.menu)  this.menu.volume  = m ? 0 : 0.5;
  }
  isMuted(): boolean {
    return this.muted;
  }

  playMusic(): void {
    this.wantMusic = true;
    if (!this.music) {
      this.music = new window.Audio("/music.mp3");
      // Manual loop (loop=false + ended handler) so each pass starts at the
      // offset too — native loop=true would replay the first 30s every cycle.
      this.music.loop = false;
      this.music.volume = this.muted ? 0 : 0.6;
      this.music.addEventListener("ended", () => {
        if (!this.music) return;
        this.music.currentTime = MUSIC_START_OFFSET;
        this.music.play().catch(() => {});
      });
    }
    if (this.music.paused) {
      this.music.currentTime = MUSIC_START_OFFSET;
      this.music.play().catch(() => {
        // Autoplay blocked — retried on next user gesture via retryPending().
      });
    }
  }

  pauseMusic(): void {
    // Note: leave wantMusic = true so retryPending picks it back up if
    // we lost playback to a backgrounding event mid-run.
    if (this.music && !this.music.paused) this.music.pause();
  }

  resumeMusic(): void {
    this.wantMusic = true;
    if (this.music && this.music.paused && !this.muted) {
      this.music.play().catch(() => {});
    }
  }

  stopMusic(): void {
    this.wantMusic = false;
    if (this.music) {
      this.music.pause();
      this.music.currentTime = 0;
    }
  }

  // ─────────────────── Menu / home-screen music ────────────────────
  // Reuse the gameplay track but loop only its intro (0 → MUSIC_START_OFFSET).
  // Gameplay starts from MUSIC_START_OFFSET, so the menu and game don't
  // overlap musically — they're two halves of the same track.
  playMenuMusic(): void {
    this.wantMenu = true;
    if (!this.menu) {
      this.menu = new window.Audio("/music.mp3");
      this.menu.loop = false;
      this.menu.volume = this.muted ? 0 : 0.5;
      // Restart at 0 a hair before we'd cross into the gameplay portion.
      // timeupdate fires every 100-250ms which is plenty precise for a
      // 30-second window — no audible seam at the boundary.
      this.menu.addEventListener("timeupdate", () => {
        if (!this.menu) return;
        if (this.menu.currentTime >= MUSIC_START_OFFSET - 0.05) {
          this.menu.currentTime = 0;
        }
      });
      this.menu.addEventListener("ended", () => {
        if (!this.menu) return;
        this.menu.currentTime = 0;
        this.menu.play().catch(() => {});
      });
    }
    if (this.menu.paused) {
      this.menu.currentTime = 0;
      this.menu.play().catch(() => {
        // Autoplay blocked — retried on next user gesture via retryPending().
      });
    }
  }

  stopMenuMusic(): void {
    this.wantMenu = false;
    if (this.menu) {
      this.menu.pause();
      this.menu.currentTime = 0;
    }
  }

  /**
   * Re-attempt any track that *should* be playing but isn't (because an
   * earlier .play() got blocked by autoplay rules). Cheap to call every
   * tap; only does real work when there's something pending.
   */
  retryPending(): void {
    if (this.wantMenu && this.menu && this.menu.paused && !this.muted) {
      this.menu.play().catch(() => {});
    }
    if (this.wantMusic && this.music && this.music.paused && !this.muted) {
      this.music.play().catch(() => {});
    }
  }

  sfx(kind: SfxKind): void {
    if (this.muted || !this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.connect(g).connect(this.master);

    switch (kind) {
      case "coin":
        o.type = "triangle";
        o.frequency.setValueAtTime(880, t);
        o.frequency.exponentialRampToValueAtTime(1320, t + 0.08);
        g.gain.setValueAtTime(0.0, t);
        g.gain.linearRampToValueAtTime(0.4, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
        o.start(t);
        o.stop(t + 0.2);
        break;
      case "powerup":
        o.type = "square";
        o.frequency.setValueAtTime(440, t);
        o.frequency.exponentialRampToValueAtTime(1760, t + 0.25);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.35, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
        o.start(t);
        o.stop(t + 0.42);
        break;
      case "crash":
        o.type = "sawtooth";
        o.frequency.setValueAtTime(220, t);
        o.frequency.exponentialRampToValueAtTime(40, t + 0.5);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.6, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
        o.start(t);
        o.stop(t + 0.72);
        break;
      case "swipe":
        o.type = "sine";
        o.frequency.setValueAtTime(660, t);
        o.frequency.exponentialRampToValueAtTime(440, t + 0.06);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.18, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
        o.start(t);
        o.stop(t + 0.12);
        break;
      case "jump":
        o.type = "sine";
        o.frequency.setValueAtTime(330, t);
        o.frequency.exponentialRampToValueAtTime(660, t + 0.15);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.25, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
        o.start(t);
        o.stop(t + 0.22);
        break;
      case "slide":
        o.type = "sawtooth";
        o.frequency.setValueAtTime(180, t);
        o.frequency.exponentialRampToValueAtTime(120, t + 0.25);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.18, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
        o.start(t);
        o.stop(t + 0.32);
        break;
      case "complete":
        // Two-tone arpeggio
        this.tone(523.25, 0.1, t);
        this.tone(659.25, 0.1, t + 0.1);
        this.tone(783.99, 0.18, t + 0.2);
        return;
    }
  }

  private tone(freq: number, dur: number, when: number): void {
    if (!this.ctx || !this.master) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(freq, when);
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(0.3, when + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, when + dur);
    o.connect(g).connect(this.master);
    o.start(when);
    o.stop(when + dur + 0.02);
  }
}
